import overpass_connection from './overpass_connection.mjs'
import overpass_ql_codes from './overpass_ql_codes.mjs'
import { elements_filtered, get_alternatives, unique } from './helpers.mjs'

const admin_levels = {
    Country: 2,
    Province: 4,
    Municipality: 6,
    Neighborhood: 9
}

export const get_center_of_neighborhood = async (municipality) => {
    let elements = []
    try {
        elements = await overpass_connection(overpass_ql_codes.get_center_of_neighborhood_code(municipality))
    } catch (error) {
        console.log(`Error: ${error.message}`)
        throw error
    }

    if(elements.length == 0) {
        return null
    }

    return elements.sort((a,b) => a.tags?.name - b.tags?.name)[0].center
}

export const get_ways_by_municipality = async (municipality) => {
    let elements = []
    try {
        elements = await overpass_connection(overpass_ql_codes.get_ways_by_municipality_code(municipality))
    } catch (error) {
        console.log(`Error: ${error.message}`)
        throw error
    }

    const ways = elements_filtered(elements)
    const unique_ways = unique(ways)

    return unique_ways.sort()
}

export const get_first_between_ways_by_main = async (main, municipality) => {
    let elements = []
    try {
        elements = await overpass_connection(overpass_ql_codes.get_first_between_ways_by_main_way_code(get_alternatives(main), municipality))
    } catch (error) {
        console.log(`Error: ${error.message}`)
        throw error
    }

    const ways = elements_filtered(elements)
    const unique_ways = unique(ways)

    return unique_ways.sort()
}

export const get_second_between_streets = async (main, first, municipality) => {
    let elements = []
    try {
        elements = await overpass_connection(overpass_ql_codes.get_second_between_way_code(get_alternatives(main), get_alternatives(first), municipality))
    } catch (error) {
        console.log(`Error: ${error.message}`)
        throw error
    }

    const ways = elements_filtered(elements)
    const unique_ways = unique(ways)

    return unique_ways.sort()
}

export const get_address_coordinate = async (main, first, second, mun) => {
    const main_street_nodes = await get_way_nodes(mun, main)
    const first_nodes = await get_way_nodes(mun, first)
    const second_nodes = await get_way_nodes(mun, second)

    const intersect1 = []
    const intersect2 = []


    main_street_nodes.forEach(n => {
        if(first_nodes.find(fn => fn.id == n.id)) intersect1.push(n);
        if(second_nodes.find(sn => sn.id == n.id)) intersect2.push(n);
    })

    if(intersect1.length == 0 || intersect2.length == 0) throw new Error('Intersections do not exists')

    const first_intersection = intersect1[0]
    const second_intersection = intersect2[0]

    const new_coordinates = calculate_coordinates_in_between(
        first_intersection.lat,
        first_intersection.lon,
        second_intersection.lat,
        second_intersection.lon
    )

    return new_coordinates
}

export const get_main_and_first_coordinate = async (main, first, mun) => {
    const main_street_nodes = await get_way_nodes(mun, main)
    const first_nodes = await get_way_nodes(mun, first)

    const intersect = []

    main_street_nodes.forEach(n => {
        if(first_nodes.find(fn => fn.id == n.id)) intersect.push(n);
    })

    if(intersect.length == 0) throw new Error('Intersection do not exists')

    return {lat: intersect[0].lat, lon: intersect[0].lon}
}

export const get_main_street_coordinate = async (main, mun) => {
    let elements = []
    try {
        elements = await overpass_connection(overpass_ql_codes.get_coordinate_by_main_street_code(mun, get_alternatives(main)))
    } catch (error) {
        throw error
    }
    if(elements.length == 0) {
        return null
    }

    return elements.sort((a,b) => a.tags?.name - b.tags?.name)[0].center
}

export const get_full_address_by_coordinate = async (lat, long) => {
    const main_street_distances = [10, 20, 30, 50, 100, 200, 500, 1000]

    let distance_to_use = 10

    for(let i = 0; i < main_street_distances.length; i++) {
        try {
            const nearest = await overpass_connection(overpass_ql_codes.get_nearest_ways_code(lat, long, main_street_distances[i]))
            if(nearest.length > 0) {
                distance_to_use = main_street_distances[i]
                break
            }
        } catch (error) {
            throw error
        }
    }

    let elements = []

    try {
        elements = await overpass_connection(overpass_ql_codes.get_full_address_elements_code(lat, long, distance_to_use))
    } catch(error) {
        throw error
    }

    let counts = 0
    let main_street_count = 0;
    let addressObj = {
        country: '',
        province: '',
        municipality: '',
        neighborhood: '',
        main_street: '',
        between_streets: []
    }

    elements.forEach(v => {
        if(v.type && v.type == 'count') {
            counts++
        } else {
            const name = v.tags?.name
            if(!name) return;
            if(counts == 1) {
                if(main_street_count < 1) {
                    addressObj.main_street = name
                }
                main_street_count++
            } else if ([2,3,4].includes(counts)) {
                if(!addressObj.between_streets.includes(name) && name != addressObj.main_street && addressObj.between_streets.length < 2)
                    addressObj.between_streets.push(name)
            } else if (counts == 5) {
                const al = v.tags?.admin_level
                if(!al) return;

                if(al == admin_levels.Country) {
                    addressObj.country = name
                    return;
                }

                if(al == admin_levels.Province) {
                    addressObj.province = name
                    return;
                }

                if(al == admin_levels.Municipality) {
                    addressObj.municipality = name
                    return;
                }

                if(al == admin_levels.Neighborhood) {
                    addressObj.neighborhood = name
                    return;
                }
            }
        }
    })

    return addressObj
}

const get_way_nodes = async (municipality, street) => {
    let elements = []

    try {
        elements = await overpass_connection(overpass_ql_codes.get_way_nodes_code(municipality, get_alternatives(street)))
    } catch (error) {
        console.log(`Error: ${error.message}`)
        throw error
    }

    const nodes = []

    elements.forEach(el => {
        if(!el.tags || el.tags.highway == 'traffic_signals') {
            nodes.push(el)
        }
    })

    return nodes
}

const calculate_coordinates_in_between = (lat1, long1, lat2, long2) => {
    return {
        lat: ((lat2 + lat1) / 2).toFixed(5),
        lon: ((long2 + long1) / 2).toFixed(5)
    }
}